import type { HttpHeaders, HttpMethod, PathParams, PathQuery } from './types'
import { resolveFullPath } from './URLUtils'
import { toAPIError } from './APIError'

export type APIRequest = {
  method: HttpMethod
  path: string
  pathParams: PathParams
  query: PathQuery
  body: unknown
  headers: HttpHeaders
  credentials?: RequestCredentials
}

export type APIResponse<TData> = {
  success: boolean
  data?: TData
  message?: string | null
  validationErrors?: string[]
}

export type APISuccess<TData, TFields extends object = NoFields> = {
  success: true
  data: TData
} & TFields

type NoFields = object

export class APIClient<TResponseFields extends object = NoFields> {
  constructor(private readonly baseUrl: string) {}

  async call<TData>(request: APIRequest): Promise<APISuccess<TData, TResponseFields>> {
    const requestUrl =
      this.baseUrl + resolveFullPath(request.path, request.pathParams, request.query)

    const headers: HeadersInit = { ...request.headers }
    let bodyOption: BodyInit | null

    if (request.body instanceof Blob) {
      bodyOption = request.body
      headers['Content-Type'] = request.body.type || 'application/octet-stream'
    } else if (request.body instanceof FormData) {
      bodyOption = request.body
    } else {
      bodyOption = request.body ? JSON.stringify(request.body) : null
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(requestUrl, {
      method: request.method,
      headers,
      body: bodyOption,
      credentials: request.credentials,
    })

    const responseData: APIResponse<TData> = await response.json()

    if (responseData.success) {
      return responseData as APISuccess<TData, TResponseFields>
    }

    throw toAPIError(response.status, responseData)
  }
}
